import { Box, Breadcrumbs, Button, FormControl, FormLabel, Input, Sheet, Stack, Typography, useTheme } from "@mui/joy";

import { useNavigate, useSearchParams } from "react-router";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import useMediaQuery from '@mui/material/useMediaQuery';

import { LayoutContext } from "../../context";
import ModalMessagePane from "../../components/MessagesChat/ModalMessagePane";
import MessagesPane from "../../components/MessagesChat/MessagesPane";
import ChatsPane from "../../components/MessagesChat/ChatsPane";
import { closeMessagesPane } from "../../utils";
import { CHAT } from "../../components/MessagesChat/chatTheme";



/**
 * Высота раздела чата = ровно столько, сколько осталось в области контента.
 *
 * Раньше высота задавалась как calc(96.5dvh - var(--Header-height)), но
 * переменной --Header-height в проекте нет вовсе: объявление становилось
 * недействительным и отбрасывалось, высота уезжала в auto, содержимое
 * вылезало за область контента — и появлялся второй, общестраничный
 * скроллбар, который таскал весь раздел целиком.
 *
 * Меряем по факту: находим ближайшего прокручиваемого предка и вычитаем
 * из его высоты расстояние до верха чата (там могут висеть плашки загрузки).
 * Пересчитываем на resize контейнера — раскрытие сайдбара и поворот
 * телефона тоже меняют картину.
 */
function useFillHeight(ref) {
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return undefined;

    let host = el.parentElement;
    while (host && host !== document.body) {
      const oy = window.getComputedStyle(host).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      host = host.parentElement;
    }
    if (!host || host === document.body) return undefined;

    const measure = () => {
      // Отступ от НАЧАЛА содержимого контейнера, а не от его видимой рамки:
      // иначе при прокрученном контейнере (сверху бывают плашки загрузки)
      // высота считалась бы заново на каждый пиксель прокрутки.
      const gap = Math.max(0, Math.round(
        el.getBoundingClientRect().top + host.scrollTop - host.getBoundingClientRect().top,
      ));
      const next = Math.max(320, Math.round(host.clientHeight - gap));
      setHeight((prev) => (prev === next ? prev : next));
    };

    measure();

    // Раздел появляется с анимацией (motion.main едет снизу вверх на 40px).
    // Первый замер попадает в середину этой анимации: верх страницы ещё
    // смещён, gap выходит больше настоящего — и внизу оставалась белая
    // полоса ровно на величину недоезда. Перемеряем, когда анимация осела.
    const timers = [60, 260, 600].map((ms) => setTimeout(measure, ms));

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(host);
    }
    window.addEventListener('resize', measure);
    return () => {
      timers.forEach(clearTimeout);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return height;
}

const useKeyPress = (targetKey, callback) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === targetKey) {
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [targetKey, callback]);
};



export default function MessagesPage() {
    const { loadsUnreadCounts, chats, setChats } = useContext(LayoutContext);

    const [ selectedChat, setSelectedChat ] = useState();
    const [ modalMessage, setModalMessage ] = useState({ open: false });
    
    const [searchParams, setSearchParams] = useSearchParams();
    const chatId = searchParams.get("chatId");

    const rootRef = useRef(null);
    const fillHeight = useFillHeight(rootRef);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 'sm' - стандартный breakpoint для мобильных (обычно 600px)

    // Выбор чата.
    //
    // Раньше здесь заранее выкачивались сообщения — и складывались в
    // selectedChat.messages, которое никто не читает: ленту грузит сам
    // MessagesPane. Лишний запрос был бы полбеды, но он же помечал чат
    // прочитанным в момент клика — то есть граница «Непрочитанные»
    // стиралась раньше, чем человек успевал что-то увидеть.
    const handleChatSelect = (_chat_data) => {
        if (!_chat_data?.chat_id) return;
        setSelectedChat({ ..._chat_data });
    };

    useEffect(() => {
        loadsUnreadCounts();
    }, []);

    // Страховка: раздел уходит — снимаем всё, что могло остаться на body.
    //
    // Сам раздел прокрутку больше не блокирует, но блокировку ставит
    // MUI-модалка переписки, а на телефоне из неё можно уйти по кнопке
    // «назад» браузера — тогда маршрут меняется, и порядок размонтирования
    // не гарантирован. Один removeProperty дешевле, чем ещё один зависший
    // экран: если блокировку поставил кто-то другой и она ещё нужна,
    // он выставит её заново при следующем открытии.
    useEffect(() => {
        return () => {
            closeMessagesPane();
            if (typeof document !== 'undefined') {
                document.body.style.removeProperty('overflow');
                document.body.style.removeProperty('padding-right');
            }
        };
    }, []);

    // Загрузка чата при монтировании, если есть ID в URL
    useEffect(() => {
        console.log("chatId", chatId);
        if (chatId && chats?.length > 0) {
            const foundChat = chats.find(itm => String(itm?.chat_id) === String(chatId));
            if (foundChat) {
                setModalMessage({ open: true });
                handleChatSelect(foundChat);
            }
            // Если чат не найден — не делаем ничего, ждём загрузки
        } else if (!chatId) {
            setSelectedChat(null);
            setModalMessage({ open: false });
        }
    }, [chatId, chats]);

    // Закрытие чата при нажатии Esc
    useKeyPress('Escape', () => {
        if (selectedChat) {
            setSelectedChat(null);
            setModalMessage({ open: false });
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("chatId");
            setSearchParams(newParams);
        } 
    });


    const handleNewChatCreated = (chatId) => {
        // Обновляем URL с новым chatId
        const newParams = new URLSearchParams(searchParams);
        newParams.set("chatId", chatId);
        setSearchParams(newParams, { replace: true });
        // navigate(`/chats?chatId=${chatId}`, { replace: true });
    };


    return (
        <Box
            ref={rootRef}
            sx={{
                display: 'flex',
                // Пока не измерили — держимся высоты экрана: так первый кадр
                // не «прыгает» и страница всё равно не получает скроллбар.
                height: fillHeight ? `${fillHeight}px` : '100dvh',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100%',
                    minHeight: 0,
                    gap: 1,
                    m: 0
                }}
            >
                <Sheet
                    sx={{
                        flex: 1,
                        width: '100%',
                        mx: 'auto',
                        display: 'grid',
                        // Ширина списка фиксирована. Раньше здесь стояло
                        // minmax(min-content, min(30%, 400px)): min-content
                        // считался по самой длинной строке карточки чата
                        // («Событие #25705 · Новый Уренгой, улица 26-го
                        // съезда КПСС, 9А»), поэтому колонка разъезжалась
                        // далеко за 400px и занимала треть экрана.
                        // minmax(0, 1fr) вместо 1fr: колонка 1fr по умолчанию
                        // имеет min-width:auto и всё равно раздувается длинным
                        // сообщением. Нулевой минимум это запрещает.
                        gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            sm: `${CHAT.listWidth}px minmax(0, 1fr)`,
                            lg: `${CHAT.listWidthLg}px minmax(0, 1fr)`,
                        },
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    {/* Список чатов — обычный блок сетки.
                        Раньше на телефоне он был position:fixed, width/height 100%
                        и z-index 100 — то есть полноэкранной панелью поверх всего,
                        включая шапку приложения. Видимость держалась на переменной
                        --MessagesPane-slideIn: уехал влево трансформом — «закрыт»,
                        вернулся — «открыт». Это наследие шаблона: тогда переписка
                        была второй такой же панелью. Сейчас переписка на телефоне
                        открывается модалкой, а сетка на xs и так одноколоночная —
                        значит фиксировать нечего. Зато после закрытия модалки этот
                        невидимый слой оставался сверху и съедал все касания: мимо
                        него не проходили ни прокрутка списка, ни нажатия по меню. */}
                    <Sheet
                        sx={{
                            position: { xs: 'relative', sm: 'sticky' },
                            zIndex: { xs: 0, sm: 100 },
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                            height: '100%',
                            minHeight: 0,
                        }}
                    >
                        <ChatsPane
                            chats={chats}
                            setChats={setChats}
                            selectedChatId={selectedChat?.chat_id}
                            setSelectedChat={setSelectedChat}
                            handleChatSelect={handleChatSelect}
                            onNewChatCreated={handleNewChatCreated}
                        />
                    </Sheet>

                
                    {/* minWidth: 0 у правой колонки обязателен: без него длинное
                        сообщение или адрес растягивают grid-ячейку и ломают вёрстку */}
                    { isMobile ?
                        <ModalMessagePane modalMessage={modalMessage} setModalMessage={setModalMessage}>
                            <MessagesPane
                                chat={selectedChat} 
                                chats={chats}
                                setChats={setChats}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                                setSelectedChat={setSelectedChat}
                            />
                        </ModalMessagePane>
                        :
                        <MessagesPane
                            chat={selectedChat} 
                            chats={chats}
                            setChats={setChats}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            setSelectedChat={setSelectedChat}
                        />
                    }
                </Sheet>
            </Box>
        </Box>
    );
}